/**
 * Состояние входа для шапки (ТЗ UI-60 … UI-62).
 *
 * Экран аккаунта спрашивает сервер сам, но шапка стоит на каждом экране, и
 * человек, который не понимает, вошёл он или нет, не пойдёт это выяснять —
 * он просто перестанет доверять сохранению прогресса. Такую находку владелец
 * уже приносил (этапы AAB–AAD).
 *
 * Запрос делается редко и никогда во время набора: при первом показе, после
 * подтверждения адреса и когда человек уходит с экрана аккаунта — там
 * состояние и меняется, регистрацией, входом или удалением.
 */

import { useEffect, useRef, useState } from 'react';
import type { SignedIn, TrialNotice } from '@type2x/feature-accounts';

import type { VerificationState } from '../state/verification.js';

/**
 * Что известно про вход.
 *
 * `unknown` — ответа ещё нет, и это отдельное состояние, а не «гость»:
 * назвать гостем того, о ком мы ещё не спросили, значит сказать неправду
 * (ТЗ UI-06).
 */
export type AccountStatus = 'unknown' | 'guest' | 'signed-in' | 'unverified';

/**
 * Что известно про аккаунт всему приложению.
 *
 * Уведомление о конце trial едет здесь же: тот же ответ сервера, тот же
 * редкий запрос. Спрашивать второй раз ради одной строки незачем, а показать
 * её нужно там, где человек читает, а не только на экране аккаунта
 * (ТЗ FR-SUB-03).
 */
export interface AccountState {
  readonly status: AccountStatus;
  readonly trialNotice: TrialNotice;
}

/** Состояние входа по ответу сервера. */
function statusOf(session: SignedIn | null): AccountStatus {
  if (session === null) return 'guest';
  return session.emailVerified ? 'signed-in' : 'unverified';
}

export function useAccountStatus(
  readSession: () => Promise<SignedIn | null>,
  verification: VerificationState,
  screenName: string,
): AccountState {
  const [state, setState] = useState<AccountState>({ status: 'unknown', trialNotice: 'none' });
  const [asked, setAsked] = useState(0);
  const previousScreen = useRef(screenName);

  // Уход с экрана аккаунта — единственный момент, когда состояние могло
  // измениться незаметно для шапки: регистрация, вход и удаление происходят
  // там.
  useEffect(() => {
    const left = previousScreen.current === 'account' && screenName !== 'account';
    previousScreen.current = screenName;
    if (left) setAsked((count) => count + 1);
  }, [screenName]);

  const verified = verification.status === 'done';

  useEffect(() => {
    let current = true;

    void readSession()
      .then((session) => {
        if (!current) return;
        setState({
          status: statusOf(session),
          trialNotice: session?.trialNotice ?? 'none',
        });
      })
      // Нет связи — нет и ответа: показывать «гость» тому, кто вошёл, хуже,
      // чем не утверждать ничего.
      .catch(() => {
        if (current) setState({ status: 'unknown', trialNotice: 'none' });
      });

    return () => {
      current = false;
    };
  }, [readSession, verified, asked]);

  return state;
}
